import type { KeyboardEvent } from 'react'
import { useEffect, useRef, useState } from 'react'

import { deleteReq, getJson, postJson, uploadImage } from '@/api/client'
import { streamTurn, type StreamHandle } from '@/api/sse'
import type {
  SessionResponse,
  StreamEvent,
  TranscriptEvent,
  TranscriptResponse,
  WaitingTipsResponse,
} from '@/api/types'
import { useAuth } from '@/auth/AuthContext'
import { clearActiveSession, getActiveSession, setActiveSession } from '@/lib/session-store'
import { AppShell } from '@/ui/AppShell'
import { Button } from '@/ui/Button'
import { TextArea } from '@/ui/TextArea'

import { CommandBar } from './CommandBar'
import { QuestionWizardModal } from './QuestionWizardModal'
import {
  buildQuestionnaireDisplayText,
  derivePendingQuestionnaire,
  isQuestionToolName,
  normalizeQuestionnairePayload,
  type PendingQuestionnaire,
  type QuestionnaireAnswerBundle,
} from './questionnaire'
import { ImageLightbox } from './ImageLightbox'
import { TurnView, type TranscriptEntry } from './TurnView'

function sessionHeadline(s: SessionResponse): string {
  if (s.title && s.title.trim()) return s.title
  return 'Untitled'
}

const POLL_INTERVAL_MS = 1500
const TITLE_REFRESH_DELAY_MS = 1000
const TITLE_REFRESH_ATTEMPTS = 30

function groupTurns(events: TranscriptEvent[]): TranscriptEntry[][] {
  const turns: TranscriptEntry[][] = []
  let current: TranscriptEntry[] = []
  for (const ev of events) {
    const displayKind =
      ev.kind === 'user_message' ? 'user' :
      ev.kind === 'assistant_text' ? 'assistant_text' :
      ev.kind === 'thinking' ? 'thinking' :
      ev.kind === 'ask_user_question' ? 'ask_user_question' :
      ev.kind === 'result' ? 'done' :
      ev.kind

    const entry: TranscriptEntry = {
      id: `${ev.ts}-${ev.kind}`,
      kind: displayKind,
      ts: ev.ts,
      text: typeof ev.payload === 'object' && ev.payload !== null
        ? (ev.payload as any).text || (ev.payload as any).content || JSON.stringify(ev.payload)
        : String(ev.payload ?? ''),
      stopReason: typeof ev.payload === 'object' && ev.payload !== null
        ? (ev.payload as any).stop_reason
        : undefined,
      cost: typeof ev.payload === 'object' && ev.payload !== null
        ? (ev.payload as any).cost_usd
        : undefined,
      isError: ev.kind === 'error',
      attachments: typeof ev.payload === 'object' && ev.payload !== null
        ? (ev.payload as any).attachments
        : undefined,
    }
    if (displayKind === 'user' || displayKind === 'turn_started') {
      if (current.length > 0) turns.push(current)
      current = [entry]
    } else {
      current.push(entry)
    }
  }
  if (current.length > 0) turns.push(current)
  return turns
}

function makeLocalId(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
}

function hasSessionTitle(current: SessionResponse | null): boolean {
  return Boolean(current?.title?.trim())
}

function formatElapsed(seconds: number): string {
  if (seconds < 60) return `${seconds}s`
  const minutes = Math.floor(seconds / 60)
  const remaining = seconds % 60
  return remaining === 0 ? `${minutes}m` : `${minutes}m ${remaining}s`
}

function getStepInfo(phase: string) {
  if (phase === 'running_tool') {
    return { step: 1, label: 'Looking things up' }
  }
  if (phase === 'streaming_model') {
    return { step: 2, label: 'Drafting your answer' }
  }
  return { step: 0, label: 'Understanding your question' }
}

function PhaseProgress({ phase }: { phase: string }) {
  const { step } = getStepInfo(phase)
  const steps = ['Understanding your question', 'Looking things up', 'Drafting your answer']
  return (
    <div className="flex items-center gap-2 mt-3">
      {steps.map((label, i) => (
        <div key={label} className="flex items-center gap-2">
          <div
            className={
              i === step
                ? 'h-2 w-2 rounded-full bg-ember animate-pulse-glow'
                : i < step
                  ? 'h-2 w-2 rounded-full bg-ember/60'
                  : 'h-2 w-2 rounded-full bg-haze/60'
            }
            title={label}
          />
          {i < steps.length - 1 && (
            <div
              className={i < step ? 'w-6 h-px bg-ember/40' : 'w-6 h-px bg-haze/40'}
            />
          )}
        </div>
      ))}
    </div>
  )
}

function LiveThinkingStatus({
  session,
  tips,
  onStop,
}: {
  session: SessionResponse | null
  tips: string[]
  onStop?: () => void
}) {
  const [tipIndex, setTipIndex] = useState(0)

  useEffect(() => {
    if (session?.turn_state !== 'running') return
    const timer = window.setInterval(() => {
      setTipIndex((value) => value + 1)
    }, 5000)
    return () => window.clearInterval(timer)
  }, [session?.turn_state])

  useEffect(() => {
    setTipIndex(0)
  }, [tips])

  if (!session || session.turn_state !== 'running') {
    return null
  }

  const startedAt = session.turn_started_at ? new Date(session.turn_started_at) : null
  const elapsedSeconds = startedAt
    ? Math.max(0, Math.floor((Date.now() - startedAt.getTime()) / 1000))
    : 0
  const tip = tips.length > 0 ? tips[tipIndex % tips.length] : null
  const activity = session.current_activity || 'Thinking'
  const { label } = getStepInfo(session.turn_phase)

  return (
    <div
      aria-live="polite"
      className="animate-fade-in rounded-2xl border border-haze/50 bg-ink-raised/60 backdrop-blur-sm px-6 py-5"
      role="status"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-full bg-ember animate-pulse" />
            <span className="h-2.5 w-2.5 rounded-full bg-ember/70 animate-pulse [animation-delay:180ms]" />
            <span className="h-2.5 w-2.5 rounded-full bg-ember/40 animate-pulse [animation-delay:360ms]" />
          </div>
          <span className="font-display italic text-sm text-cream">
            {activity}
          </span>
          {session.current_tool_name && (
            <span className="text-xs px-3 py-1 rounded-full bg-haze/40 text-mute border border-haze/50">
              {session.current_tool_name}
            </span>
          )}
        </div>
        {onStop && (
          <button
            onClick={onStop}
            className="text-xs text-mute hover:text-ember transition-colors font-display px-3 py-1 rounded-full border border-haze/50 hover:border-ember/50"
            type="button"
          >
            Stop
          </button>
        )}
      </div>
      <PhaseProgress phase={session.turn_phase} />
      <p className="mt-2 text-xs text-mute font-display">{label}</p>
      {tip && (
        <p className="mt-3 text-sm leading-relaxed text-cream/85">
          {tip}
        </p>
      )}
      <p className="mt-3 text-xs text-mute/70">
        Elapsed {formatElapsed(elapsedSeconds)}
      </p>
    </div>
  )
}

export function ChatPage() {
  const { user } = useAuth()
  const [sessions, setSessions] = useState<SessionResponse[]>([])
  const [session, setSession] = useState<SessionResponse | null>(null)
  const [turns, setTurns] = useState<TranscriptEntry[][]>([])
  const [input, setInput] = useState('')
  const [streaming, setStreaming] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [paletteOpen, setPaletteOpen] = useState(false)
  const [waitingTips, setWaitingTips] = useState<string[]>([])
  const [pendingQuestionnaire, setPendingQuestionnaire] = useState<PendingQuestionnaire | null>(null)
  const [questionnaireSubmitting, setQuestionnaireSubmitting] = useState(false)
  interface Attachment {
    path: string
    previewUrl: string
  }

  const [attachedImages, setAttachedImages] = useState<Attachment[]>([])
  const attachedImagesRef = useRef(attachedImages)
  useEffect(() => { attachedImagesRef.current = attachedImages }, [attachedImages])
  const [imageUploadError, setImageUploadError] = useState<string | null>(null)
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null)
  const pollTimerRef = useRef<number | null>(null)
  const titleTimerRef = useRef<number | null>(null)
  const streamRef = useRef<StreamHandle | null>(null)
  const sessionRef = useRef<SessionResponse | null>(null)
  const streamingRef = useRef(false)
  const questionnaireSubmittingRef = useRef(false)
  const streamAbortExpectedRef = useRef(false)
  const waitingTipsRequestRef = useRef(0)
  const pollBusyRef = useRef(false)
  const endRef = useRef<HTMLDivElement | null>(null)
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  async function loadSessions() {
    try {
      const rows = await getJson<SessionResponse[]>('/v1/sessions')
      setSessions(rows)
    } catch (err) {
      console.error('Failed to load sessions', err)
    }
  }

  useEffect(() => {
    sessionRef.current = session
  }, [session])

  useEffect(() => {
    streamingRef.current = streaming
  }, [streaming])

  useEffect(() => {
    questionnaireSubmittingRef.current = questionnaireSubmitting
  }, [questionnaireSubmitting])

  useEffect(() => {
    return () => {
      attachedImagesRef.current.forEach((a) => URL.revokeObjectURL(a.previewUrl))
    }
  }, [])

  function applyTranscript(data: TranscriptResponse) {
    setSession(data.session)
    setTurns(groupTurns(data.events))
    const derivedQuestionnaire = derivePendingQuestionnaire(data.events)
    if (derivedQuestionnaire) {
      setPendingQuestionnaire(derivedQuestionnaire)
      setQuestionnaireSubmitting(false)
      return
    }
    if (data.session.turn_state !== 'running') {
      setPendingQuestionnaire(null)
      setQuestionnaireSubmitting(false)
      return
    }
    if (!questionnaireSubmittingRef.current) {
      setPendingQuestionnaire(null)
    }
  }

  async function loadTranscript(sessionId: string): Promise<TranscriptResponse | null> {
    try {
      const data = await getJson<TranscriptResponse>(`/v1/sessions/${sessionId}`)
      applyTranscript(data)
      return data
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load transcript')
      return null
    }
  }

  function stopPolling() {
    if (pollTimerRef.current) {
      window.clearInterval(pollTimerRef.current)
      pollTimerRef.current = null
    }
  }

  function stopTitleRefresh() {
    if (titleTimerRef.current) {
      window.clearTimeout(titleTimerRef.current)
      titleTimerRef.current = null
    }
  }

  function abortActiveStream() {
    streamAbortExpectedRef.current = true
    streamRef.current?.abort()
    streamRef.current = null
  }

  function resetWaitingTips() {
    waitingTipsRequestRef.current += 1
    setWaitingTips([])
  }

  async function requestWaitingTips(prompt: string, phase?: string, toolName?: string | null) {
    const requestId = ++waitingTipsRequestRef.current
    try {
      const data = await postJson<WaitingTipsResponse>('/v1/waiting-tips', {
        prompt,
        phase: phase ?? '',
        tool_name: toolName ?? null,
      })
      if (waitingTipsRequestRef.current !== requestId) return
      setWaitingTips(data.tips)
    } catch {
      if (waitingTipsRequestRef.current !== requestId) return
      setWaitingTips([])
    }
  }

  async function syncTranscript(
    sessionId: string,
    options?: { refreshListOnIdle?: boolean },
  ): Promise<TranscriptResponse | null> {
    const data = await loadTranscript(sessionId)
    if (!data) return null

    if (data.session.turn_state !== 'running') {
      stopPolling()
      setStreaming(false)
      resetWaitingTips()
      if (options?.refreshListOnIdle) {
        void loadSessions()
      }
    }

    return data
  }

  function startPolling(
    sessionId: string,
    options?: { immediate?: boolean },
  ) {
    stopPolling()

    const tick = async () => {
      if (pollBusyRef.current) return
      pollBusyRef.current = true
      try {
        await syncTranscript(sessionId, { refreshListOnIdle: true })
      } finally {
        pollBusyRef.current = false
      }
    }

    if (options?.immediate !== false) {
      void tick()
    }

    pollTimerRef.current = window.setInterval(() => {
      void tick()
    }, POLL_INTERVAL_MS)
  }

  function refreshTitleSoon(sessionId: string, attempt = 0) {
    if (attempt >= TITLE_REFRESH_ATTEMPTS) return

    stopTitleRefresh()
    titleTimerRef.current = window.setTimeout(async () => {
      const current = sessionRef.current
      if (!current || current.session_id !== sessionId || hasSessionTitle(current)) return

      const data = await loadTranscript(sessionId)
      void loadSessions()

      if (data && !hasSessionTitle(data.session)) {
        refreshTitleSoon(sessionId, attempt + 1)
      }
    }, TITLE_REFRESH_DELAY_MS)
  }

  async function loadAndAttach(sessionId: string): Promise<boolean> {
    stopTitleRefresh()
    const data = await loadTranscript(sessionId)
    if (!data) return false

    if (data.session.turn_state === 'running') {
      setStreaming(true)
      startPolling(sessionId)
      void requestWaitingTips('', data.session.turn_phase, data.session.current_tool_name)
    } else {
      stopPolling()
      setStreaming(false)
    }

    return true
  }

  useEffect(() => {
    void loadSessions()
    const saved = getActiveSession()
    if (saved) {
      void loadAndAttach(saved)
    }
    return () => {
      abortActiveStream()
      stopPolling()
      stopTitleRefresh()
    }
  }, [])

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [turns, streaming])

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setPaletteOpen((v) => !v)
      }
    }
    window.addEventListener('keydown', onKeyDown as unknown as EventListener)
    return () => window.removeEventListener('keydown', onKeyDown as unknown as EventListener)
  }, [])

  async function createSession() {
    abortActiveStream()
    stopPolling()
    stopTitleRefresh()
    setError(null)
    setStreaming(false)
    setPendingQuestionnaire(null)
    setQuestionnaireSubmitting(false)
    resetWaitingTips()
    try {
      const s = await postJson<SessionResponse>('/v1/sessions', { title: '' })
      setSession(s)
      setTurns([])
      setActiveSession(s.session_id)
      await loadSessions()
      textareaRef.current?.focus()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create session')
    }
  }

  async function pickSession(s: SessionResponse) {
    if (sessionRef.current?.session_id === s.session_id) return

    abortActiveStream()
    stopPolling()
    stopTitleRefresh()
    setError(null)
    setStreaming(false)
    setPendingQuestionnaire(null)
    setQuestionnaireSubmitting(false)
    resetWaitingTips()
    const ok = await loadAndAttach(s.session_id)
    if (ok) {
      setActiveSession(s.session_id)
    }
  }

  function pushToLatestTurn(entry: TranscriptEntry) {
    setTurns((prev) => {
      const next = prev.map((turn) => [...turn])
      if (next.length === 0) {
        next.push([entry])
      } else {
        next[next.length - 1].push(entry)
      }
      return next
    })
  }

  function markLatestPendingQuestionAnswered(answer: string) {
    setTurns((prev) => {
      const next = prev.map((turn) => [...turn])
      for (let i = next.length - 1; i >= 0; i -= 1) {
        const hasQuestion = next[i].some((entry) => entry.kind === 'ask_user_question')
        const hasAnswer = next[i].some((entry) => entry.kind === 'question_answered')
        if (!hasQuestion || hasAnswer) continue
        next[i].push({
          id: makeLocalId('question-answered'),
          kind: 'question_answered',
          ts: new Date().toISOString(),
          text: answer,
        })
        break
      }
      return next
    })
  }

  function appendLiveText(kind: 'assistant' | 'thinking', text: string) {
    setTurns((prev) => {
      const next = prev.map((turn) => [...turn])
      if (next.length === 0) {
        next.push([])
      }

      const currentTurn = next[next.length - 1]
      const last = currentTurn[currentTurn.length - 1]
      if (last && last.kind === kind) {
        currentTurn[currentTurn.length - 1] = {
          ...last,
          text: `${last.text ?? ''}${text}`,
        }
      } else {
        currentTurn.push({
          id: makeLocalId(kind),
          kind,
          ts: new Date().toISOString(),
          text,
        })
      }

      return next
    })
  }

  async function finalizeTurn(sessionId: string) {
    const data = await syncTranscript(sessionId, { refreshListOnIdle: true })
    if (!data) {
      stopPolling()
      setStreaming(false)
      return
    }

    if (data.session.turn_state === 'running') {
      setStreaming(true)
      startPolling(sessionId)
      return
    }

    if (!hasSessionTitle(data.session)) {
      refreshTitleSoon(sessionId)
    }
  }

  async function recoverFromStreamDrop(sessionId: string) {
    const data = await syncTranscript(sessionId, { refreshListOnIdle: true })
    if (!data) {
      stopPolling()
      setStreaming(false)
      setError('Live stream interrupted and transcript reload failed')
      return
    }

    setError(null)
    if (data.session.turn_state === 'running') {
      setStreaming(true)
      startPolling(sessionId)
      if (waitingTips.length === 0) {
        void requestWaitingTips('', data.session.turn_phase, data.session.current_tool_name)
      }
    }
  }

  function handleStreamEvent(sessionId: string, ev: StreamEvent) {
    switch (ev.event) {
      case 'session':
        setSession((prev) => (
          prev && prev.session_id === sessionId
            ? { ...prev, sdk_session_id: ev.data.sdk_session_id }
            : prev
        ))
        break
      case 'phase':
        setSession((prev) => (
          prev && prev.session_id === sessionId
            ? {
              ...prev,
              turn_state: 'running',
              turn_phase: ev.data.phase,
              current_activity: ev.data.activity,
              current_tool_name: ev.data.tool_name,
              turn_started_at: prev.turn_started_at ?? new Date().toISOString(),
            }
            : prev
        ))
        break
      case 'assistant_delta':
        appendLiveText('assistant', ev.data.text)
        break
      case 'thinking_delta':
        appendLiveText('thinking', ev.data.text)
        break
      case 'tool_use':
        if (isQuestionToolName(ev.data.name)) {
          const questionnaire = normalizeQuestionnairePayload(ev.data.input)
          if (questionnaire) {
            setPendingQuestionnaire(questionnaire)
          }
          setQuestionnaireSubmitting(false)
          pushToLatestTurn({
            id: makeLocalId('ask'),
            kind: 'ask_user_question',
            ts: new Date().toISOString(),
            text: JSON.stringify(questionnaire ?? ev.data.input),
          })
        }
        break
      case 'tool_result':
        break
      case 'error':
        pushToLatestTurn({
          id: makeLocalId('error'),
          kind: 'error',
          ts: new Date().toISOString(),
          text: ev.data.message,
          isError: true,
        })
        setError(ev.data.message)
        setStreaming(false)
        streamRef.current = null
        void finalizeTurn(sessionId)
        break
      case 'done':
        pushToLatestTurn({
          id: makeLocalId('done'),
          kind: 'done',
          ts: new Date().toISOString(),
          stopReason: ev.data.stop_reason ?? undefined,
          cost: ev.data.cost_usd ?? undefined,
        })
        setStreaming(false)
        streamRef.current = null
        void finalizeTurn(sessionId)
        break
    }
  }

  async function sendTurn(
    content: string,
    options?: {
      clearInput?: boolean
      displayContent?: string
      waitingTipPrompt?: string
      attachments?: string[]
    },
  ): Promise<boolean> {
    const currentSession = sessionRef.current
    const text = content.trim()
    const displayText = (options?.displayContent ?? content).trim()
    const attachments = options?.attachments ?? []
    if (!text) {
      return false
    }
    if (!currentSession) {
      setError('No active session')
      return false
    }
    if (streamingRef.current || currentSession.turn_state === 'running') {
      setError('Please wait for the current response to finish')
      return false
    }

    if (options?.clearInput !== false) {
      setInput('')
    }
    stopTitleRefresh()
    setError(null)
    setStreaming(true)
    setSession((prev) => (
      prev && prev.session_id === currentSession.session_id
        ? {
          ...prev,
          turn_state: 'running',
          turn_phase: 'starting',
          turn_started_at: new Date().toISOString(),
          current_activity: 'accepted request',
          current_tool_name: null,
        }
        : prev
    ))
    resetWaitingTips()
    void requestWaitingTips((options?.waitingTipPrompt ?? displayText), 'starting', null)
    markLatestPendingQuestionAnswered(displayText)

    setTurns((prev) => [
      ...prev,
      [{
        id: makeLocalId('user'),
        kind: 'user',
        ts: new Date().toISOString(),
        text: displayText,
        attachments,
      }],
    ])

    streamAbortExpectedRef.current = false

    try {
      streamRef.current = streamTurn(
        currentSession.session_id,
        {
          content: text,
          display_content: displayText,
          attachments,
        },
        (ev) => handleStreamEvent(currentSession.session_id, ev),
        (err) => {
          streamRef.current = null
          const aborted = err instanceof Error && err.name === 'AbortError'
          if (streamAbortExpectedRef.current || aborted) {
            streamAbortExpectedRef.current = false
            return
          }
          if (questionnaireSubmittingRef.current) {
            setQuestionnaireSubmitting(false)
          }
          setError(err instanceof Error ? err.message : String(err))
          void recoverFromStreamDrop(currentSession.session_id)
        },
      )
      void streamRef.current.done.catch(() => {})
      return true
    } catch (err) {
      streamRef.current = null
      stopPolling()
      setStreaming(false)
      if (questionnaireSubmittingRef.current) {
        setQuestionnaireSubmitting(false)
      }
      setError(err instanceof Error ? err.message : 'Failed to send message')
      throw err
    }
  }

  async function handleQuestionnaireSubmit(
    bundle: QuestionnaireAnswerBundle,
    displayText: string,
  ): Promise<boolean> {
    setQuestionnaireSubmitting(true)
    try {
      const started = await sendTurn(JSON.stringify(bundle), {
        clearInput: false,
        displayContent: displayText || buildQuestionnaireDisplayText(bundle),
        waitingTipPrompt: displayText,
      })
      if (!started) {
        setQuestionnaireSubmitting(false)
      }
      return started
    } catch {
      setQuestionnaireSubmitting(false)
      return false
    }
  }

  async function stopTurn() {
    const currentSession = sessionRef.current
    if (!currentSession) return
    try {
      await postJson<SessionResponse>(`/v1/sessions/${currentSession.session_id}/stop`, {})
      stopPolling()
      setStreaming(false)
      resetWaitingTips()
      await syncTranscript(currentSession.session_id)
    } catch (err) {
      console.error('Failed to stop turn', err)
    }
  }

  async function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const files = e.target.files
    if (!files || files.length === 0) return
    const currentSession = sessionRef.current
    if (!currentSession) {
      setImageUploadError('No active session')
      return
    }

    setImageUploadError(null)
    const newAttachments: Attachment[] = []
    for (const file of Array.from(files)) {
      const previewUrl = URL.createObjectURL(file)
      try {
        const result = await uploadImage(currentSession.session_id, file)
        newAttachments.push({ path: result.path, previewUrl })
      } catch (err) {
        URL.revokeObjectURL(previewUrl)
        setImageUploadError(err instanceof Error ? err.message : 'Upload failed')
      }
    }
    if (newAttachments.length > 0) {
      setAttachedImages((prev) => [...prev, ...newAttachments])
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  function removeAttachment(path: string) {
    setAttachedImages((prev) => {
      const removed = prev.find((a) => a.path === path)
      if (removed) URL.revokeObjectURL(removed.previewUrl)
      return prev.filter((a) => a.path !== path)
    })
  }

  function sendMessage() {
    void sendTurn(input, { attachments: attachedImages.map((a) => a.path) })
    attachedImages.forEach((a) => URL.revokeObjectURL(a.previewUrl))
    setAttachedImages([])
  }

  function handleKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      void sendTurn(input, { attachments: attachedImages.map((a) => a.path) })
      attachedImages.forEach((a) => URL.revokeObjectURL(a.previewUrl))
      setAttachedImages([])
    }
  }

  async function handleDeleteSession(s: SessionResponse) {
    if (!confirm('Delete this session?')) return
    try {
      await deleteReq(`/v1/sessions/${s.session_id}`)
      if (session?.session_id === s.session_id) {
        abortActiveStream()
        stopPolling()
        stopTitleRefresh()
        setSession(null)
        setTurns([])
        setStreaming(false)
        setPendingQuestionnaire(null)
        setQuestionnaireSubmitting(false)
        resetWaitingTips()
        clearActiveSession()
      }
      await loadSessions()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed')
    }
  }

  const isAdmin = user?.role === 'admin'
  const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform)
  const isQuestionPending = Boolean(pendingQuestionnaire)

  return (
    <AppShell onOpenPalette={() => setPaletteOpen(true)}>
      <div className="flex flex-col h-full bg-ink">
        {!session ? (
          <div className="flex-1 flex items-center justify-center p-12">
            <div className="max-w-lg animate-fade-in text-center">
              {/* Decorative floating shapes */}
              <div className="relative w-32 h-32 mx-auto mb-10">
                <div className="absolute inset-0 bg-ember/10 rounded-full animate-float-slow" />
                <div className="absolute inset-4 bg-ember/5 rounded-full animate-float" style={{ animationDelay: '1s' }} />
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="font-display text-5xl italic text-ember/40">b</span>
                </div>
              </div>

              <div className="flex items-center justify-center gap-3 font-sans text-xs text-mute mb-8">
                <span className="w-6 h-px bg-haze/60" />
                <span>Welcome</span>
                <span className="w-6 h-px bg-haze/60" />
              </div>
              <h2 className="font-display text-5xl font-medium text-cream leading-[1.05] tracking-tight mb-6">
                Your personal <span className="italic text-ember">beauty</span> consultant.
              </h2>
              <p className="text-mute text-base leading-relaxed mb-12 max-w-md mx-auto">
                Ask about skincare, routines, ingredients, or anything you'd like a second opinion on.
              </p>
              <div className="flex items-center justify-center gap-4">
                <Button size="lg" onClick={createSession}>
                  Start a consultation
                </Button>
                <button
                  onClick={() => setPaletteOpen(true)}
                  className="text-sm text-mute hover:text-cream transition-colors duration-300"
                >
                  or browse past ones <kbd className="ml-1 font-mono text-[11px] border border-haze/60 px-1.5 py-0.5 rounded-md">{isMac ? '⌘K' : 'Ctrl K'}</kbd>
                </button>
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="shrink-0 border-b border-haze/40 px-6 lg:px-10 py-3.5">
              <div className="max-w-3xl mx-auto flex items-center gap-4">
                <span className="w-2 h-2 rounded-full bg-ember shrink-0" />
                <h1 className="font-display text-base text-cream truncate flex-1">
                  {sessionHeadline(session)}
                </h1>
                <button
                  onClick={() => setPaletteOpen(true)}
                  className="text-xs text-mute hover:text-cream transition-colors duration-300 hidden sm:flex items-center gap-1.5"
                >
                  switch
                  <kbd className="font-mono text-[10px] border border-haze/60 px-1 rounded-md">{isMac ? '⌘K' : 'Ctrl K'}</kbd>
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-6 lg:px-10 py-10">
              <div className="max-w-3xl mx-auto space-y-10">
                {turns.map((turn, i) => (
                  <div key={i}>
                    <TurnView turn={turn} isStreaming={streaming && i === turns.length - 1} sessionId={session?.session_id} />
                  </div>
                ))}
                {(streaming || session?.turn_state === 'running') && (
                  <LiveThinkingStatus
                    session={session}
                    tips={waitingTips}
                    onStop={session?.turn_state === 'running' ? stopTurn : undefined}
                  />
                )}
                <div ref={endRef} />
              </div>
            </div>

            <div className="shrink-0 border-t border-haze/40 px-6 lg:px-10 py-5">
              <div className="max-w-3xl mx-auto">
                {error && (
                  <div className="mb-4 text-sm text-ember border-l-[3px] border-ember pl-4 py-2 bg-ember/5 rounded-r-xl">
                    {error}
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={handleFileSelect}
                />
                <div className="flex items-end gap-3">
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={streaming || isQuestionPending}
                    className="h-12 w-12 shrink-0 flex items-center justify-center bg-ink-raised border border-haze/60 text-mute hover:text-cream hover:border-ember transition-all duration-300 rounded-full disabled:opacity-40"
                    type="button"
                    title="Attach image"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>
                  </button>
                  <TextArea
                    ref={textareaRef}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Ask anything…"
                    rows={1}
                    className="flex-1 text-base italic resize-none min-h-[48px] max-h-[240px] font-display rounded-2xl border-haze/60 bg-ink-raised/80"
                    disabled={streaming || isQuestionPending}
                  />
                  {session?.turn_state === 'running' ? (
                    <Button
                      variant="ghost"
                      size="md"
                      onClick={stopTurn}
                      className="h-12 shrink-0 rounded-full"
                    >
                      Stop
                    </Button>
                  ) : (
                    <Button
                      size="md"
                      onClick={sendMessage}
                      disabled={(!input.trim() && attachedImages.length === 0) || streaming || isQuestionPending}
                      className="h-12 shrink-0 rounded-full"
                    >
                      Ask
                    </Button>
                  )}
                </div>
                {attachedImages.length > 0 && (
                  <div className="flex flex-wrap gap-3 mt-3">
                    {attachedImages.map((a) => (
                      <div key={a.path} className="relative group">
                        <img
                          src={a.previewUrl}
                          alt="Attachment preview"
                          className="h-24 w-24 object-cover rounded-xl border border-haze/50 cursor-pointer hover:border-ember transition-all duration-300"
                          onClick={() => setLightboxSrc(a.previewUrl)}
                        />
                        <button
                          onClick={() => removeAttachment(a.path)}
                          className="absolute -top-1.5 -right-1.5 h-5 w-5 flex items-center justify-center bg-ink text-mute hover:text-ember border border-haze/60 rounded-full transition-all duration-300"
                          type="button"
                          title="Remove attachment"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                {lightboxSrc && (
                  <ImageLightbox
                    src={lightboxSrc}
                    alt="Attachment preview"
                    onClose={() => setLightboxSrc(null)}
                  />
                )}
                {imageUploadError && (
                  <div className="mt-2 text-xs text-ember">
                    {imageUploadError}
                  </div>
                )}
                <div className="flex items-center justify-between mt-3">
                  <p className="text-xs text-mute/70">
                    Enter to send · Shift + Enter for a new line
                  </p>
                  {isAdmin && session && (
                    <button
                      onClick={() => handleDeleteSession(session)}
                      className="text-xs text-mute hover:text-ember transition-colors duration-300"
                    >
                      Delete session
                    </button>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      <CommandBar
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        sessions={sessions}
        activeId={session?.session_id ?? null}
        onPickSession={pickSession}
        onNewSession={createSession}
      />
      {pendingQuestionnaire && (
        <QuestionWizardModal
          questionnaire={pendingQuestionnaire}
          submitting={questionnaireSubmitting}
          onSubmit={handleQuestionnaireSubmit}
        />
      )}
    </AppShell>
  )
}
